import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import {
  GetEmailCampaignDraft,
  PostEmailCampaignDraftFromTemplate,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilder";
import { GetEmailMarketingTemplates } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import type { IEmailMarketingTemplateListItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplatesTypes";
import { EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT } from "pages/secure/facility/marketing/emailBuilder/emailBuilderConstants";
import { EmailBuilderTemplatePickerCard } from "pages/secure/facility/marketing/emailBuilder/templatePicker/EmailBuilderTemplatePickerCard";
import { selectEmailBuilderTemplateForCampaign } from "pages/secure/facility/marketing/emailBuilder/templatePicker/selectEmailBuilderTemplateForCampaign";
import { emailCampaignBuilderPath } from "pages/secure/facility/marketing/emailBuilder/launchEmailCampaignBuilder";
import "pages/secure/facility/marketing/emailBuilder/templatePicker/emailBuilderTemplatePicker.scss";
import { StatusCode } from "api/protocols";

type TEmailCampaignTemplatePickerProps = {
  campaignId: number;
  returnTo: string;
};

type TTemplatePickerLoadState =
  | { status: "loading" }
  | { status: "failed"; message: string }
  | {
      status: "succeeded";
      system: IEmailMarketingTemplateListItem[];
      facility: IEmailMarketingTemplateListItem[];
    };

function renderTemplatePickerGrid(
  templates: IEmailMarketingTemplateListItem[],
  options: {
    selecting: boolean;
    onSelect: (templateId: number) => void;
  },
) {
  return (
    <div className="email-builder-template-picker__grid">
      {templates.map(template => (
        <EmailBuilderTemplatePickerCard
          key={template.id}
          template={template}
          disabled={options.selecting}
          onSelect={templateId => {
            options.onSelect(templateId);
          }}
        />
      ))}
    </div>
  );
}

export default function EmailCampaignTemplatePicker(props: TEmailCampaignTemplatePickerProps) {
  const { campaignId, returnTo } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const selectingRef = useRef(false);
  const [selecting, setSelecting] = useState(false);
  const [loadState, setLoadState] = useState<TTemplatePickerLoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const templatesRes = await GetEmailMarketingTemplates(
        {
          limit: EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT,
          offset: 0,
        },
        false,
      );

      if (cancelled) {
        return;
      }

      if (templatesRes.status !== StatusCode.OK || templatesRes.data == null) {
        setLoadState({
          status: "failed",
          message:
            typeof templatesRes.message === "string" && templatesRes.message.length > 0
              ? templatesRes.message
              : t("Could not load email templates."),
        });
        return;
      }

      setLoadState({
        status: "succeeded",
        system: templatesRes.data.system,
        facility: templatesRes.data.facility,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  async function handleSelectTemplate(templateId: number) {
    if (campaignId < 1 || selectingRef.current) {
      return;
    }

    selectingRef.current = true;
    setSelecting(true);

    const result = await selectEmailBuilderTemplateForCampaign({
      campaignId,
      templateId,
      createFromTemplate: PostEmailCampaignDraftFromTemplate,
      getDraft: GetEmailCampaignDraft,
    });

    if (result.ok === false) {
      dispatch(showError(t(result.message)));
      selectingRef.current = false;
      setSelecting(false);
      return;
    }

    history.push(emailCampaignBuilderPath(campaignId), { returnTo });

    selectingRef.current = false;
    setSelecting(false);
  }

  if (loadState.status === "loading") {
    return (
      <div className="email-builder-template-picker">
        <div className="email-builder-template-picker__status" aria-busy="true">
          <p className="email-builder-template-picker__status-text">{t("Loading templates…")}</p>
        </div>
      </div>
    );
  }

  if (loadState.status === "failed") {
    return (
      <div className="email-builder-template-picker">
        <div className="email-builder-template-picker__error">
          <p className="email-builder-template-picker__error-text">{loadState.message}</p>
        </div>
      </div>
    );
  }

  if (loadState.system.length === 0 && loadState.facility.length === 0) {
    return (
      <div className="email-builder-template-picker">
        <div className="email-builder-template-picker__error">
          <p className="email-builder-template-picker__error-text">{t("No templates available.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-builder-template-picker">
      {loadState.system.length > 0
        ? renderTemplatePickerGrid(loadState.system, {
            selecting,
            onSelect: templateId => {
              void handleSelectTemplate(templateId);
            },
          })
        : null}

      {loadState.facility.length > 0 ? (
        <section
          className="email-builder-template-picker__section"
          aria-labelledby="email-campaign-template-picker-my-templates-heading"
        >
          <h2
            id="email-campaign-template-picker-my-templates-heading"
            className="email-builder-template-picker__section-title"
          >
            {t("My templates")}
          </h2>
          {renderTemplatePickerGrid(loadState.facility, {
            selecting,
            onSelect: templateId => {
              void handleSelectTemplate(templateId);
            },
          })}
        </section>
      ) : null}
    </div>
  );
}
